// completion.ts - schema-driven YAML completion, ported from
// apps/cloud/static_src/js/dashboard/yaml-completions.js.
//
// Two layers live here. The framework-agnostic *core* (context detection,
// schema indexing, candidate building, auto-trigger decision) touches no
// CodeMirror API - kept pure so it stays easy to test and reuse. The
// *adapter* (`dbtChartsCompletionSource`, `dbtChartsCompletionExtensions`) maps
// that core onto `@codemirror/autocomplete`.
//
// The completion catalog is a required parameter (`dbtChartsCompletionExtensions(catalog)`)
// - this package never fetches it and never assumes a global.

import {
  type Completion,
  type CompletionContext as CMCompletionContext,
  type CompletionResult,
  acceptCompletion,
  autocompletion,
  closeCompletion,
  pickedCompletion,
  startCompletion,
} from "@codemirror/autocomplete";
import { getIndentUnit, indentString } from "@codemirror/language";
import { countColumn, type Extension, Prec } from "@codemirror/state";
import { EditorView, keymap } from "@codemirror/view";

// A deliberate module cycle (value_chips.ts imports the completion core back);
// every cross-use is inside a function body, so resolution is safe at runtime.
import { dbtChartsValueChips } from "./value_chips.js";

const WORD_RE = /[A-Za-z0-9_-]/;
const BRANCH_KEYS = ["oneOf", "anyOf", "allOf"] as const;
const ROOT_PATH = "$";
const WILDCARD_PATH = "*";
const ARRAY_ITEM_PATH = "[]";
const MAX_INDEX_DEPTH = 8;

type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

/** Marks an `allOf` entry stating a cross-field rule rather than key shapes. */
const CROSS_FIELD_RULE = "x-dbt-charts-cross-field-rule";

/** A node in a JSON-Schema-shaped completion catalog. */
export interface SchemaNode {
  $ref?: string;
  description?: string;
  enum?: JsonValue[];
  const?: string;
  type?: string | string[];
  // JSON imports widen sibling objects with `key?: undefined`.
  properties?: Record<string, SchemaNode | undefined>;
  additionalProperties?: SchemaNode | boolean;
  items?: SchemaNode;
  oneOf?: SchemaNode[];
  anyOf?: SchemaNode[];
  allOf?: SchemaNode[];
  if?: SchemaNode;
  then?: SchemaNode;
  not?: SchemaNode;
  required?: string[];
  [CROSS_FIELD_RULE]?: boolean;
}

/** `oneOf`/`anyOf`/`allOf` members, minus cross-field rules: they constrain
 * which keys may appear together, never which keys or values exist. */
function branchesOf(node: SchemaNode, branchKey: (typeof BRANCH_KEYS)[number]): SchemaNode[] {
  return (node[branchKey] ?? []).filter((branch) => !branch[CROSS_FIELD_RULE]);
}

/** The root completion catalog: a schema node plus its `$ref` targets. */
export interface CompletionSchema extends SchemaNode {
  $defs?: Record<string, SchemaNode>;
}

export interface EditorPosition {
  line: number;
  ch: number;
}

export interface CompletionCoreContext {
  mode: "key" | "value";
  token: string;
  path: string[];
  valuePath: string[];
  /** Scalar key:value pairs keyed by the document path of their YAML mapping. */
  discriminatorValues: Record<string, Record<string, string> | undefined>;
}

/**
 * The three insertion shapes a key can expect. `classifyKind` is the only
 * place this gets decided.
 */
export type ValueKind = "scalar" | "object" | "list";

interface KeyRule {
  kinds: Record<string, ValueKind>;
}

interface ValueRule {
  values: string[];
  localRef: string | null;
}

const EMPTY_KEY_RULE: KeyRule = { kinds: {} };
const EMPTY_VALUE_RULE: ValueRule = { values: [], localRef: null };

function mergeKeyRules(first: KeyRule, second: KeyRule): KeyRule {
  return { kinds: { ...second.kinds, ...first.kinds } };
}

function mergeValueRules(first: ValueRule, second: ValueRule): ValueRule {
  return { values: [...first.values, ...second.values], localRef: first.localRef ?? second.localRef };
}

// Values are `| undefined` even though `Record` doesn't require it by default:
// these maps are built incrementally by path key, so a lookup for a path the
// schema never populated is a real, expected miss at runtime.
export interface CompletionIndex {
  keys: Record<string, KeyRule | undefined>;
  values: Record<string, ValueRule | undefined>;
  aliases: Record<string, string | undefined>;
  /**
   * Maps `pathKey(unionPath)` to the discriminator field name for any path
   * where a discriminated `allOf` union was indexed. Used by `lookupRule` to
   * splice `@discriminator=value` into the lookup path when the cursor's
   * sibling scalars reveal the chosen family.
   */
  discriminated: Record<string, string | undefined>;
}

/**
 * A candidate the core produced. A key-mode item always carries the `kind`
 * `classifyKind` assigned it during indexing - a key candidate cannot exist
 * without one. A value-mode item is a leaf string with no shape decision to
 * make.
 */
export type CompletionItem =
  | { mode: "key"; displayText: string; kind: ValueKind }
  | { mode: "value"; displayText: string };

export interface CompletionListResult {
  list: CompletionItem[];
}

function uniqueSorted(values: string[]): string[] {
  return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b));
}

function lineIndent(line: string): number {
  const match = /^ */.exec(line);
  return match ? match[0].length : 0;
}

function keyFromLine(line: string): string | null {
  const match = /^ *([A-Za-z0-9_-]+):(?:\s|$)/.exec(line);
  return match ? match[1] : null;
}

const LIST_ITEM_RE = /^( *)-( +|$)/;

/**
 * The ancestor-path contribution(s) a single source line makes, as
 * `(visual indent, key)` pairs in shallow-to-deep order. A plain "key:" line
 * contributes one pair at its own indent, same as before. A list-item line
 * ("- key: ...") contributes two: the array-item marker `[]` at the dash's
 * own indent, and - when the item opens a key on the same line - that key at
 * the visual indent YAML gives content after the dash: the dash's own indent
 * plus one (the dash itself) plus however many literal spaces follow it. A
 * bare "- " (no key on the line) contributes only the `[]` marker.
 */
function lineContributions(line: string): { indent: number; key: string }[] {
  const listItem = LIST_ITEM_RE.exec(line);
  if (!listItem) {
    const key = keyFromLine(line);
    return key ? [{ indent: lineIndent(line), key }] : [];
  }
  const dashIndent = listItem[1].length;
  const afterDash = listItem[2];
  const contributions = [{ indent: dashIndent, key: ARRAY_ITEM_PATH }];
  const key = keyFromLine(line.slice(listItem[0].length));
  if (key) {
    contributions.push({ indent: dashIndent + 1 + afterDash.length, key });
  }
  return contributions;
}

function tokenBeforeCursor(line: string, ch: number): string {
  let start = ch;
  while (start > 0 && WORD_RE.test(line[start - 1])) {
    start -= 1;
  }
  return line.slice(start, ch);
}

function pathKey(path: string[]): string {
  return path.length ? path.join(".") : ROOT_PATH;
}

function pathBeforeLine(lines: string[], lineNumber: number, indent: number): { indent: number; key: string }[] {
  const path: { indent: number; key: string }[] = [];
  for (let index = 0; index < lineNumber; index += 1) {
    const line = lines[index];
    if (!line.trim() || line.trimStart().startsWith("#")) {
      continue;
    }
    for (const { indent: currentIndent, key } of lineContributions(line)) {
      while (path.length && path[path.length - 1].indent >= currentIndent) {
        path.pop();
      }
      if (currentIndent < indent) {
        path.push({ indent: currentIndent, key });
      }
    }
  }
  return path;
}

function currentValueKey(line: string, ch: number): string | null {
  const beforeCursor = line.slice(0, ch);
  const match = /^ *([A-Za-z0-9_-]+):\s*[^#]*$/.exec(beforeCursor);
  return match ? match[1] : null;
}

function isArrayItemValue(line: string, ch: number): boolean {
  return /^ *-\s*[A-Za-z0-9_-]*$/.test(line.slice(0, ch));
}

/**
 * Extracts a scalar `key: value` pair from a single YAML line, returning
 * `{indent, key, value, valueStart}` or null — `valueStart` is the value's
 * character offset within the line, for callers (value chips) that decorate
 * the value rather than read it. Handles both plain `key: value` lines and
 * list-item-opened `- key: value` lines (using the same effective-indent
 * formula as `lineContributions`). Only non-empty values are extracted: a
 * bare `key:` opener (block mapping or empty scalar) returns null.
 */
export function scalarPairOnLine(
  line: string,
): { indent: number; key: string; value: string; valueStart: number } | null {
  const plain = /^( *)([A-Za-z0-9_-]+):[ \t]+(\S.*)$/.exec(line);
  // The value group runs to end-of-line, so its start is the line length
  // minus the group length — no re-derivation of the prefix widths.
  if (plain) {
    return {
      indent: plain[1].length,
      key: plain[2],
      value: plain[3].trimEnd(),
      valueStart: line.length - plain[3].length,
    };
  }
  const listItem = LIST_ITEM_RE.exec(line);
  if (listItem) {
    const rest = line.slice(listItem[0].length);
    const restMatch = /^([A-Za-z0-9_-]+):[ \t]+(\S.*)$/.exec(rest);
    if (restMatch) {
      return {
        indent: listItem[1].length + 1 + listItem[2].length,
        key: restMatch[1],
        value: restMatch[2].trimEnd(),
        valueStart: line.length - restMatch[2].length,
      };
    }
  }
  return null;
}

/**
 * Scans both before and after `lineNumber` collecting scalar `key: value`
 * pairs whose effective indent equals `indent` (the target mapping's direct
 * children level). Stops scanning in each direction at the first non-blank
 * non-comment line whose raw indent is below `indent` - that signals we have
 * left the enclosing block. The cursor's own line is skipped (it is mid-edit).
 *
 * Collect-then-stop: a list-item opener (`  - key: value`) whose raw indent
 * is below `indent` but whose effective content indent equals `indent` is
 * collected before the stop fires, so the opener's own scalar pair is not
 * missed.
 */
function siblingScalarsAt(lines: string[], lineNumber: number, indent: number): Record<string, string> {
  const result: Record<string, string> = {};
  for (const direction of [-1, 1] as const) {
    let i = lineNumber + direction;
    while (i >= 0 && i < lines.length) {
      const line = lines[i];
      if (!line.trim() || line.trimStart().startsWith("#")) {
        i += direction;
        continue;
      }
      const pair = scalarPairOnLine(line);
      if (pair !== null && pair.indent === indent && !(pair.key in result)) {
        result[pair.key] = pair.value;
      }
      if (lineIndent(line) < indent) break;
      i += direction;
    }
  }
  return result;
}

export function completionContextAt(source: string, pos: EditorPosition): CompletionCoreContext {
  const lines = source.split("\n");
  const line = lines[pos.line] ?? "";
  const indent = lineIndent(line);
  const pathEntries = pathBeforeLine(lines, pos.line, indent);
  const path = pathEntries.map((item) => item.key);
  const token = tokenBeforeCursor(line, pos.ch);
  const valueKey = currentValueKey(line, pos.ch);
  const arrayItem = isArrayItemValue(line, pos.ch);
  const discriminatorValues: CompletionCoreContext["discriminatorValues"] = {};
  discriminatorValues[ROOT_PATH] = siblingScalarsAt(lines, pos.line, pathEntries[0]?.indent ?? indent);
  for (let index = 0; index < pathEntries.length; index += 1) {
    const mappingPath = path.slice(0, index + 1);
    const childIndent = pathEntries[index + 1]?.indent ?? indent;
    discriminatorValues[pathKey(mappingPath)] = siblingScalarsAt(lines, pos.line, childIndent);
  }
  if (arrayItem) {
    return { mode: "value", token, path, valuePath: [...path, ARRAY_ITEM_PATH], discriminatorValues };
  }
  if (valueKey) {
    return { mode: "value", token, path, valuePath: [...path, valueKey], discriminatorValues };
  }
  return { mode: "key", token, path, valuePath: [], discriminatorValues };
}

export function extractDefinitions(source: string, section: string): string[] {
  const lines = source.split("\n");
  const result: string[] = [];
  let insideSection = false;

  for (const line of lines) {
    if (!line.trim() || line.trimStart().startsWith("#")) {
      continue;
    }
    const indent = lineIndent(line);
    const key = keyFromLine(line);
    if (indent === 0) {
      insideSection = key === section;
      continue;
    }
    if (insideSection && indent === 2 && key) {
      result.push(key);
    }
  }
  return result;
}

// `{"$ref": "#"}` is the document root: a nested board inside a layout list.
const ROOT_REF = "#";

function refName(node: SchemaNode | null | undefined): string | null {
  const prefix = "#/$defs/";
  if (node?.$ref === ROOT_REF) return ROOT_REF;
  return node?.$ref?.startsWith(prefix) ? node.$ref.slice(prefix.length) : null;
}

function resolveSchemaNode(
  root: CompletionSchema,
  node: SchemaNode | null | undefined,
  seenRefs: Set<string>,
): SchemaNode | null {
  const name = refName(node);
  if (!name) {
    return node ?? null;
  }
  if (seenRefs.has(name)) {
    return null;
  }
  seenRefs.add(name);
  return name === ROOT_REF ? root : (root.$defs?.[name] ?? null);
}

function schemaDescriptions(root: CompletionSchema, node: SchemaNode | null | undefined, seenRefs = new Set<string>()): string[] {
  const resolved = resolveSchemaNode(root, node, seenRefs);
  if (!resolved) {
    return [];
  }
  const descriptions = resolved.description ? [resolved.description] : [];
  for (const branchKey of BRANCH_KEYS) {
    for (const branch of branchesOf(resolved, branchKey)) {
      descriptions.push(...schemaDescriptions(root, branch, new Set(seenRefs)));
    }
  }
  return descriptions;
}

function isBooleanTyped(node: SchemaNode): boolean {
  const types = Array.isArray(node.type) ? node.type : node.type ? [node.type] : [];
  return types.includes("boolean");
}

/**
 * Enum values plus, for a boolean-typed node, the synthesized literals
 * `true`/`false` - the schema declares the domain via `type` rather than
 * `enum`, but the two are equally bounded value sets, so both land in the
 * same value-rule slot `indexValueRule` records.
 */
function schemaEnums(root: CompletionSchema, node: SchemaNode | null | undefined, seenRefs = new Set<string>()): string[] {
  const resolved = resolveSchemaNode(root, node, seenRefs);
  if (!resolved) {
    return [];
  }
  // Enum members may be non-string literals (e.g. link's {enum: [false]});
  // candidates are always the authored YAML text, so stringify here.
  const values = (resolved.enum ?? []).map(String);
  if (isBooleanTyped(resolved)) {
    values.push("true", "false");
  }
  for (const branchKey of BRANCH_KEYS) {
    for (const branch of branchesOf(resolved, branchKey)) {
      values.push(...schemaEnums(root, branch, new Set(seenRefs)));
    }
  }
  return values;
}

function localReferenceSection(root: CompletionSchema, node: SchemaNode | null | undefined): string | null {
  const description = schemaDescriptions(root, node).join(" ").toLowerCase();
  if (description.includes("chart name") || description.includes("chart reference")) {
    return "charts";
  }
  if (description.includes("query name") || description.includes("named query")) {
    return "queries";
  }
  return null;
}

function mergeKinds(sets: Set<ValueKind>[]): Set<ValueKind> {
  const merged = new Set<ValueKind>();
  for (const set of sets) {
    for (const kind of set) {
      merged.add(kind);
    }
  }
  return merged;
}

function hasObjectShape(node: SchemaNode): boolean {
  return Boolean(node.properties) || (node.additionalProperties !== undefined && node.additionalProperties !== false);
}

/**
 * The kind signals a single resolved node contributes on its own, plus
 * whatever its `oneOf`/`anyOf`/`allOf` branches contribute (through `$ref`).
 * `type` (with `"null"` stripped) is the primary signal: over the real
 * generated catalog, 105 nodes carry `"type": "array"` against only 22 with
 * `items`, so structure alone would miss most array-typed keys. Structural
 * fields (`properties`/`additionalProperties`/`items`/`enum`) still
 * contribute, so a node with no explicit `type` is still classifiable.
 * An unresolvable `$ref` or a `$ref` cycle contributes no signal at all - * `resolveSchemaNode` returns null and this returns the empty set - which
 * `classifyKind`'s ambiguity rule then resolves to `scalar`.
 */
function kindSignals(root: CompletionSchema, node: SchemaNode | null | undefined, seenRefs: Set<string>): Set<ValueKind> {
  const resolved = resolveSchemaNode(root, node, seenRefs);
  if (!resolved) {
    return new Set();
  }

  const own = new Set<ValueKind>();
  const types = Array.isArray(resolved.type) ? resolved.type : resolved.type ? [resolved.type] : [];
  for (const type of types) {
    if (type === "null") continue;
    if (type === "object") own.add("object");
    else if (type === "array") own.add("list");
    else own.add("scalar");
  }
  if (hasObjectShape(resolved)) own.add("object");
  if (resolved.items) own.add("list");
  if (resolved.enum) own.add("scalar");

  const branchSets = [own];
  for (const branchKey of BRANCH_KEYS) {
    for (const branch of branchesOf(resolved, branchKey)) {
      branchSets.push(kindSignals(root, branch, new Set(seenRefs)));
    }
  }
  return mergeKinds(branchSets);
}

/**
 * Classifies a schema node into the one insertion shape a key completion
 * needs. The ambiguity rule, stated once: exactly one signal wins; anything
 * else - a mixed union, a signal-less node, an unresolvable or cyclic `$ref`
 * - classifies `scalar`. `key: ` is the only insertion that is a valid
 * document prefix for every branch of a mixed union, so `scalar` can never
 * produce a wrong shape. This never throws and never guesses per call: the
 * same node always classifies the same way.
 */
export function classifyKind(root: CompletionSchema, node: SchemaNode | null | undefined): ValueKind {
  const signals = kindSignals(root, node, new Set());
  return signals.size === 1 ? [...signals][0] : "scalar";
}

function addKeyKind(table: Record<string, KeyRule | undefined>, path: string[], name: string, kind: ValueKind): void {
  const key = pathKey(path);
  const current = table[key] ?? { kinds: {} };
  current.kinds[name] = kind;
  table[key] = current;
}

function addValueRule(
  table: Record<string, ValueRule | undefined>,
  path: string[],
  values: string[],
  localRef: string | null = null,
): void {
  const key = pathKey(path);
  const current = table[key] ?? { values: [], localRef: null };
  current.values = uniqueSorted([...current.values, ...values]);
  current.localRef = current.localRef ?? localRef;
  table[key] = current;
}

function indexValueRule(root: CompletionSchema, index: CompletionIndex, path: string[], node: SchemaNode): void {
  const values = schemaEnums(root, node);
  const localRef = localReferenceSection(root, node);
  if (values.length || localRef) {
    addValueRule(index.values, path, values, localRef);
  }
}

/**
 * Records that path `fromKey` is governed by the same rule as `toKey` -
 * written when the cycle guard in `indexSchemaNode` refuses to expand a
 * recursive `$ref` a second time. A self-alias (the cycle closing back onto
 * its own canonical path) is a no-op rather than a stored entry, so
 * `resolveAlias`'s loop guard never even has to fire on the common case.
 */
function recordAlias(index: CompletionIndex, fromKey: string, toKey: string): void {
  if (fromKey === toKey) {
    return;
  }
  index.aliases[fromKey] = toKey;
}

/**
 * `path` followed by each reduction of its longest aliased prefix to the
 * alias target. Every step may carry rules of its own (a layout item is both
 * an inline chart and a nested board), so lookups merge the whole chain.
 * Terminates on cyclic alias data.
 */
function aliasChain(aliases: Record<string, string | undefined>, path: string[]): string[][] {
  const chain = [path];
  const visitedKeys = new Set([pathKey(path)]);
  for (;;) {
    const current = chain[chain.length - 1];
    let reduced: string[] | null = null;
    for (let len = current.length; len >= 1; len -= 1) {
      const target = aliases[pathKey(current.slice(0, len))];
      if (target !== undefined) {
        reduced = [...(target === ROOT_PATH ? [] : target.split(".")), ...current.slice(len)];
        break;
      }
    }
    if (reduced === null || visitedKeys.has(pathKey(reduced))) {
      return chain;
    }
    visitedKeys.add(pathKey(reduced));
    chain.push(reduced);
  }
}

/**
 * Checks whether an `allOf` branch node is a discriminated branch - one
 * shaped `{if: {properties: {D: {enum: [v, ...]}}, required: [D]}, then: <schema>}`.
 * Returns the discriminator field name and its matching values, or null for
 * any other shape. Only branches where the `if` pins exactly one property to
 * a non-empty const/enum qualify.
 */
function discriminatedBranch(branch: SchemaNode): { discriminator: string; values: string[] } | null {
  const ifNode = branch.if;
  const thenNode = branch.then;
  if (!ifNode || !thenNode || !ifNode.properties) return null;
  const propNames = Object.keys(ifNode.properties);
  if (propNames.length !== 1) return null;
  const discriminator = propNames[0];
  const propSchema = ifNode.properties[discriminator];
  if (!propSchema) return null;
  const values = (propSchema.enum ?? (propSchema.const !== undefined ? [propSchema.const] : [])).map(String);
  return values.length ? { discriminator, values } : null;
}

/**
 * Indexes a schema node at `path`, threading a per-traversal `canonicalPaths`
 * map from `$ref` definition name to the first path where that definition was
 * successfully expanded. A recursive definition (its own `$ref` reachable
 * from itself, directly or through a chain of definitions) hits the
 * `seenRefs` cycle guard on every path deeper than that first expansion; here
 * that no longer means "no rule" - it means "the same rule as the canonical
 * path", recorded as an alias `lookupRule` follows.
 *
 * `allOf` branches shaped `{if: {properties: {D: {enum: [...]}}}, then: <ref>}`
 * are discriminated: the `then` schema is indexed at `[...path, "@D=v"]` per
 * tag value `v`, and `index.discriminated[pathKey(path)]` is set to `D`. The
 * synthetic segment `@D=v` contains `@` and `=` which `keyFromLine` never
 * produces, so no collision with document-derived path segments is possible.
 * Non-discriminated `allOf` members and all `oneOf`/`anyOf` members are
 * recursed at the same `path` as before.
 */
function indexSchemaNode(
  root: CompletionSchema,
  index: CompletionIndex,
  node: SchemaNode | null | undefined,
  path: string[] = [],
  seenRefs = new Set<string>(),
  depth = 0,
  canonicalPaths = new Map<string, string>(),
): void {
  if (depth > MAX_INDEX_DEPTH) {
    return;
  }

  const name = refName(node);
  const cameFromCycle = name !== null && seenRefs.has(name);
  const resolved = resolveSchemaNode(root, node, seenRefs);
  if (!resolved) {
    if (cameFromCycle && name) {
      const canonical = canonicalPaths.get(name);
      if (canonical !== undefined) {
        recordAlias(index, pathKey(path), canonical);
      }
    }
    return;
  }
  if (name && !canonicalPaths.has(name)) {
    canonicalPaths.set(name, pathKey(path));
  }

  // oneOf and anyOf: recurse branches at the same path (union of signals).
  for (const branchKey of ["oneOf", "anyOf"] as const) {
    for (const branch of branchesOf(resolved, branchKey)) {
      indexSchemaNode(root, index, branch, path, new Set(seenRefs), depth, canonicalPaths);
    }
  }

  // allOf: discriminated branches (`{if, then}` pinning one property) are
  // indexed at synthetic `@discriminator=value` sub-paths; non-discriminated
  // members are recursed at the same path as oneOf/anyOf above.
  for (const branch of branchesOf(resolved, "allOf")) {
    const disc = discriminatedBranch(branch);
    if (disc) {
      index.discriminated[pathKey(path)] = disc.discriminator;
      for (const v of disc.values) {
        indexSchemaNode(
          root,
          index,
          branch.then,
          [...path, `@${disc.discriminator}=${v}`],
          new Set(seenRefs),
          depth,
          canonicalPaths,
        );
      }
    } else {
      indexSchemaNode(root, index, branch, path, new Set(seenRefs), depth, canonicalPaths);
    }
  }

  const properties = resolved.properties ?? {};
  for (const [propName, property] of Object.entries(properties)) {
    if (!property) continue;
    addKeyKind(index.keys, path, propName, classifyKind(root, property));
    const propertyPath = [...path, propName];
    indexValueRule(root, index, propertyPath, property);
    indexSchemaNode(root, index, property, propertyPath, new Set(seenRefs), depth + 1, canonicalPaths);
  }

  if (resolved.additionalProperties && resolved.additionalProperties !== true) {
    const wildcardPath = [...path, WILDCARD_PATH];
    // A free-form map's VALUES are as bounded as a named property's whenever
    // its value schema carries an enum - `style.palettes.<role>` takes a
    // palette name, a category pin takes a `category[N]` slot. Indexing only
    // the child keys here, as this branch used to, left those value slots
    // with no candidates while the identical named property completed fine.
    indexValueRule(root, index, wildcardPath, resolved.additionalProperties);
    indexSchemaNode(
      root,
      index,
      resolved.additionalProperties,
      wildcardPath,
      new Set(seenRefs),
      depth + 1,
      canonicalPaths,
    );
  }

  if (resolved.items) {
    const itemPath = [...path, ARRAY_ITEM_PATH];
    indexValueRule(root, index, itemPath, resolved.items);
    indexSchemaNode(root, index, resolved.items, itemPath, new Set(seenRefs), depth + 1, canonicalPaths);
  }
}

export function completionIndexFromSchema(schema: CompletionSchema): CompletionIndex {
  const index: CompletionIndex = { keys: {}, values: {}, aliases: {}, discriminated: {} };
  indexSchemaNode(schema, index, { $ref: ROOT_REF });
  return index;
}

function pathCandidates(path: string[]): { path: string[]; wildcardCount: number }[] {
  const candidates: { path: string[]; wildcardCount: number }[] = [];
  function visit(index: number, candidate: string[], wildcardCount: number): void {
    if (index === path.length) {
      candidates.push({ path: candidate, wildcardCount });
      return;
    }
    visit(index + 1, [...candidate, path[index]], wildcardCount);
    if (path[index] !== ARRAY_ITEM_PATH) {
      visit(index + 1, [...candidate, WILDCARD_PATH], wildcardCount + 1);
    }
  }
  visit(0, [], 0);
  return candidates.sort((left, right) => left.wildcardCount - right.wildcardCount);
}

/**
 * Returns lookup paths with discriminator segments applied at every matching
 * ancestor mapping. Each discriminator also retains an unspliced fallback, so
 * an unknown inner discriminator can still use a valid outer family rule.
 * Document paths remain un-wildcarded and contain no synthetic segments; they
 * select the scalar scope corresponding to each schema candidate ancestor.
 */
function pathsWithDiscriminators(
  index: CompletionIndex,
  discriminatorValues: CompletionCoreContext["discriminatorValues"],
  documentContainer: string[],
  schemaContainer: string[],
): string[][] {
  function applyAt(mappingPath: string[], documentPath: string[]): string[][] {
    const discriminator = aliasChain(index.aliases, mappingPath)
      .map((path) => index.discriminated[pathKey(path)])
      .find((found) => found !== undefined);
    const value = discriminator === undefined ? undefined : discriminatorValues[pathKey(documentPath)]?.[discriminator];
    if (discriminator === undefined || value === undefined) return [mappingPath];
    return [[...mappingPath, `@${discriminator}=${value}`], mappingPath];
  }

  let paths = applyAt([], []);
  for (let segment = 0; segment < schemaContainer.length; segment += 1) {
    const documentPath = documentContainer.slice(0, segment + 1);
    paths = paths.flatMap((current) => applyAt([...current, schemaContainer[segment]], documentPath));
  }
  return paths;
}

/**
 * Looks up the rule for `path` in `table`, splicing schema-declared
 * discriminator segments into every matching ancestor mapping from shallow to
 * deep. More-specific discriminated paths win; undiscriminated fallbacks remain
 * available when a selected value has no indexed branch.
 *
 * `tailLength` is 0 when `path` IS the container being discriminated (key
 * mode, array-item merge), and 1 when the final segment is a field name
 * appended to the container (value mode, valueBounded check). The splice
 * always inserts between the container and the tail.
 *
 * Alias reduction applies to both the candidate and the spliced path, so
 * recursive-definition aliases compose correctly with discriminated lookup.
 */
function lookupRule<T>(
  table: Record<string, T | undefined>,
  index: CompletionIndex,
  discriminatorValues: CompletionCoreContext["discriminatorValues"],
  path: string[],
  tailLength: 0 | 1,
  empty: T,
  merge: (first: T, second: T) => T,
): T {
  for (const candidate of pathCandidates(path)) {
    const documentContainer = tailLength ? path.slice(0, -tailLength) : path;
    const schemaContainer = tailLength ? candidate.path.slice(0, -tailLength) : candidate.path;
    const tail = tailLength ? candidate.path.slice(-tailLength) : [];
    for (const container of pathsWithDiscriminators(index, discriminatorValues, documentContainer, schemaContainer)) {
      const lookupPath = [...container, ...tail];
      const rules = aliasChain(index.aliases, lookupPath).flatMap((path): T[] => {
        const rule = table[pathKey(path)];
        return rule === undefined ? [] : [rule];
      });
      if (rules.length) return rules.reduce((first, second) => merge(first, second));
    }
  }
  return empty;
}

export function buildCompletionList(
  index: CompletionIndex,
  source: string,
  context: CompletionCoreContext,
  token: string = context.token,
): CompletionListResult {
  const lowerToken = token.toLowerCase();
  if (context.mode === "key") {
    const rule = lookupRule(index.keys, index, context.discriminatorValues, context.path, 0, EMPTY_KEY_RULE, mergeKeyRules);
    const names = uniqueSorted(Object.keys(rule.kinds)).filter((name) => name.toLowerCase().startsWith(lowerToken));
    return { list: names.map((name): CompletionItem => ({ mode: "key", displayText: name, kind: rule.kinds[name] })) };
  }
  const rule = lookupRule(index.values, index, context.discriminatorValues, context.valuePath, 1, EMPTY_VALUE_RULE, mergeValueRules);
  const values = [...rule.values];
  if (rule.localRef) {
    values.push(...extractDefinitions(source, rule.localRef));
  }
  const names = uniqueSorted(values).filter((value) => value.toLowerCase().startsWith(lowerToken));
  const valueItems = names.map((displayText): CompletionItem => ({ mode: "value", displayText }));

  // A list item commonly opens an object ("- key: ..."), so a bare "- " is
  // simultaneously a value slot (a string item, e.g. a chart-name reference)
  // and a key slot (the item's own object keys). Only an array-item value
  // path carries this ambiguity - a plain "key: " slot never does - so the
  // key-mode merge is gated on the same "[]" marker that made this a value
  // context in the first place.
  if (context.valuePath[context.valuePath.length - 1] !== ARRAY_ITEM_PATH) {
    return { list: valueItems };
  }
  const keyRule = lookupRule(index.keys, index, context.discriminatorValues, context.valuePath, 0, EMPTY_KEY_RULE, mergeKeyRules);
  const keyNames = uniqueSorted(Object.keys(keyRule.kinds)).filter((name) => name.toLowerCase().startsWith(lowerToken));
  const keyItems = keyNames.map((name): CompletionItem => ({ mode: "key", displayText: name, kind: keyRule.kinds[name] }));
  return { list: [...keyItems, ...valueItems] };
}

/**
 * Pure mapping from a classified candidate to the text CM6 should splice in.
 * `childIndent` is the whitespace for one nested level below the current
 * line; the caller builds it from the editor's own indent unit
 * (`getIndentUnit`/`indentString`) at query time - never a literal here, so
 * this stays a plain function of its inputs.
 */
export function insertionFor(item: CompletionItem, childIndent: string): string {
  if (item.mode === "value") {
    return item.displayText;
  }
  switch (item.kind) {
    case "object":
      return `${item.displayText}:\n${childIndent}`;
    case "list":
      return `${item.displayText}:\n${childIndent}- `;
    case "scalar":
      return `${item.displayText}: `;
  }
}

const TYPED_TRIGGER_RE = /^[A-Za-z0-9_:-]$/;
const EMPTY_VALUE_SLOT_RE = /^\s*[A-Za-z0-9_-]+:[ \t]+$/;
const BLANK_INDENTED_LINE_RE = /^[ \t]+$/;

/**
 * Whether a just-typed insertion should reopen the completion tooltip.
 * `picked` is true when the triggering transaction was itself a completion
 * accept (the `pickedCompletion` annotation) - that path reopens through
 * `activateOnCompletion` instead, so this always returns false for it. No
 * default: a caller must state whether the edit was a pick.
 */
export function shouldAutoTriggerHint(inserted: string, lineBeforeCursor: string, picked: boolean): boolean {
  if (picked) {
    return false;
  }
  if (TYPED_TRIGGER_RE.test(inserted)) {
    return true;
  }
  // Any edit that leaves the cursor on a line holding nothing but indentation
  // opens the whole key list for that level: the newline that starts a fresh
  // line, and the backspace that clears a token you typed and found nothing
  // under. There is no prefix to type-ahead against there, so without this you
  // can only discover a key by guessing its first letter - which is how a
  // nested key (`orientation`, under `style:`) reads as missing when you probe
  // for it at the level above. Column 0 is deliberately excluded: a blank line
  // there is a separator between top-level blocks far more often than the
  // start of a key.
  if (BLANK_INDENTED_LINE_RE.test(lineBeforeCursor)) {
    return true;
  }
  if (!inserted) {
    return false;
  }
  // The space that lands the cursor in an empty value slot ("theme: ") should
  // surface the value list immediately - empty slots have nothing to
  // type-ahead against, so otherwise you can't see the choices without
  // guessing a letter.
  return /^[ \t]$/.test(inserted) && EMPTY_VALUE_SLOT_RE.test(lineBeforeCursor);
}

// --- CM6 adapter -------------------------------------------------------------

/**
 * A `Completion` tagged with the kind its key expects and, for a key
 * completion, whether that key's own value domain is bounded (an enum or a
 * boolean) - both read by `activateOnCompletion` below to decide whether
 * accepting this option should reopen the menu.
 */
interface DbtChartsCompletion extends Completion {
  valueKind?: ValueKind;
  valueBounded?: boolean;
}

/**
 * Accepting a key reopens the menu when the key's value is itself another
 * menu to pick from: an object (child keys) or a bounded scalar domain (enum
 * or boolean values). An unbounded scalar (free-text string, number) or a
 * list has nothing to offer immediately, so it does not reopen.
 */
function shouldReopenAfterAccept(completion: Completion): boolean {
  const { valueKind, valueBounded } = completion as DbtChartsCompletion;
  return valueKind === "object" || valueBounded === true;
}

/**
 * A `@codemirror/autocomplete` CompletionSource over a schema index.
 * Translates the cursor position into the core's `{line, ch}`, runs the
 * core, and maps its items onto CM6 `{label, apply, type}` options. Returns
 * null when the schema offers nothing so CM6 shows no tooltip.
 */
export function dbtChartsCompletionSource(index: CompletionIndex) {
  return (context: CMCompletionContext): CompletionResult | null => {
    const { state, pos } = context;
    const line = state.doc.lineAt(pos);
    const source = state.doc.toString();
    // A non-empty single-line selection over a bounded value slot is the
    // browse-the-domain gesture (a value chip's click, or select +
    // open-completions): the whole domain comes back unfiltered, replacing
    // the whole selection — a prefix filter over an already-complete value
    // would show only the value itself. Auto-triggers collapse the
    // selection, so typing never lands here.
    const selection = state.selection.main;
    if (!selection.empty && state.doc.lineAt(selection.from).to >= selection.to) {
      const selLine = state.doc.lineAt(selection.from);
      // Only a selection lying inside the line's value span is the browse
      // gesture: a whole-line selection (triple-click) also reads as
      // mode "value" at its end, and replacing it would take the key with it.
      const pair = scalarPairOnLine(selLine.text);
      const valueFrom = pair === null ? -1 : selLine.from + pair.valueStart;
      const valueTo = pair === null ? -1 : valueFrom + pair.value.length;
      const inValueSpan =
        pair !== null && selection.from >= valueFrom && selection.to <= valueTo;
      const selCore = completionContextAt(source, {
        line: selLine.number - 1,
        ch: selection.to - selLine.from,
      });
      if (inValueSpan && selCore.mode === "value") {
        const { list } = buildCompletionList(index, source, selCore, "");
        const values = list.filter((item) => item.mode === "value");
        if (values.length) {
          return {
            from: selection.from,
            to: selection.to,
            filter: false,
            options: values.map((item) => ({ label: item.displayText, apply: item.displayText, type: "value" })),
          };
        }
      }
    }
    const core = completionContextAt(source, { line: line.number - 1, ch: pos - line.from });
    const { list } = buildCompletionList(index, source, core);
    if (!list.length) {
      return null;
    }
    const from = pos - core.token.length;
    const startColumn = countColumn(line.text, state.tabSize, from - line.from);
    const childIndent = indentString(state, startColumn + getIndentUnit(state));
    // A list-item position can offer both key and value candidates in one
    // list (buildCompletionList's array-item merge), so the CM6 "type" icon
    // and the bounded-domain lookup are per item, not per context. A key
    // candidate's owner path is `core.path` in ordinary key mode, or
    // `core.valuePath` when it's a merged key candidate at an array-item
    // value slot.
    const keyBasePath = core.mode === "key" ? core.path : core.valuePath;
    const options: DbtChartsCompletion[] = list.map((item) => ({
      label: item.displayText,
      apply: insertionFor(item, childIndent),
      type: item.mode === "key" ? "property" : "value",
      valueKind: item.mode === "key" ? item.kind : undefined,
      valueBounded:
        item.mode === "key"
          ? lookupRule(index.values, index, core.discriminatorValues, [...keyBasePath, item.displayText], 1, EMPTY_VALUE_RULE, mergeValueRules).values.length > 0
          : undefined,
    }));
    return {
      from,
      options,
      validFor: new RegExp(`^${WORD_RE.source}*$`),
    };
  };
}

/**
 * Enter on a line holding nothing but indentation means "new line", even with
 * the menu open. That menu was opened for the user (by the fresh-line rule in
 * `shouldAutoTriggerHint`) rather than typed into, so it is a reference list,
 * and upstream's Enter would commit whichever option it happened to preselect
 * the moment you tried to leave a blank line. Closing it and returning false
 * hands the key to the newline command below; `Tab` accepts instead. Once any
 * prefix is typed the line is no longer blank, so upstream's Enter is back in
 * charge and accept-on-Enter works as it always did.
 */
function newlineOnBlankLine(view: EditorView): boolean {
  const head = view.state.selection.main.head;
  const line = view.state.doc.lineAt(head);
  if (!BLANK_INDENTED_LINE_RE.test(view.state.doc.sliceString(line.from, head))) {
    return false;
  }
  closeCompletion(view);
  return false;
}

/**
 * The completion extension bundle for a dbt charts YAML editor: the schema
 * source, the one explicit open-completions binding this package owns
 * (`Mod-Shift-Space` - resolves to Cmd-Shift-Space on mac, confirmed to reach
 * the page where plain `Ctrl-Shift-Space` does not, and to Ctrl-Shift-Space
 * elsewhere; `Ctrl-Space` is claimed by the macOS input-source switcher and
 * `Cmd-Space` by Spotlight; upstream's own bindings, including `Ctrl-Space`
 * and mac `Alt-\``/`Alt-i`, still arrive with `autocompletion()` wherever the
 * OS doesn't take them), the two bindings the fresh-line menu needs (`Enter`
 * keeps inserting a newline on a blank line, `Tab` accepts), and an update
 * listener that opens the tooltip while typing schema tokens, on the space
 * that opens an empty value slot, or on a newline that lands on a fresh
 * indented line. `activateOnTyping` is off so the core's
 * `shouldAutoTriggerHint` is the single trigger decision.
 * `activateOnCompletion` reopens the menu
 * after accepting an object-kind key or a key whose value is itself a
 * bounded domain (enum or boolean), so the next choice shows up without a
 * second keypress. `catalog` is a required parameter - this package never
 * fetches it.
 */
export function dbtChartsCompletionExtensions(catalog: CompletionSchema): Extension[] {
  const index = completionIndexFromSchema(catalog);
  const source = dbtChartsCompletionSource(index);
  return [
    // The clickable-value affordance shares this index, so a chip appears
    // exactly where the menu has options (value_chips.ts).
    ...dbtChartsValueChips(index),
    // `autocompletion()` mounts its own keymap at `Prec.highest`, so these two
    // have to sit at the same precedence *and* earlier in the tree to outrank
    // its Enter (accept) and reach Tab before the app's own indent binding.
    Prec.highest(
      keymap.of([
        { key: "Enter", run: newlineOnBlankLine },
        { key: "Tab", run: acceptCompletion },
      ]),
    ),
    autocompletion({
      override: [source],
      activateOnTyping: false,
      activateOnCompletion: shouldReopenAfterAccept,
    }),
    keymap.of([{ key: "Mod-Shift-Space", run: startCompletion }]),
    EditorView.updateListener.of((update) => {
      if (!update.docChanged) return;
      // Deletions count as well as insertions: backspacing a token that
      // matched nothing puts the cursor back on a blank line, which is a slot
      // the list should fill. Programmatic edits (AI patches, save
      // round-trips) carry no user event and never open the menu.
      if (!update.transactions.some((tr) => tr.isUserEvent("input") || tr.isUserEvent("delete"))) return;
      const picked = update.transactions.some((tr) => tr.annotation(pickedCompletion) !== undefined);
      let inserted = "";
      update.changes.iterChanges((_fromA, _toA, _fromB, _toB, ins) => {
        inserted += ins.toString();
      });
      const head = update.state.selection.main.head;
      const line = update.state.doc.lineAt(head);
      const lineBeforeCursor = update.state.doc.sliceString(line.from, head);
      if (shouldAutoTriggerHint(inserted, lineBeforeCursor, picked)) {
        startCompletion(update.view);
      }
    }),
  ];
}
